import React from 'react'
import { IconMessageReply, IconX } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'

interface QuotedTextProps {
  text: string
  messageId?: string
  onClose: () => void
  /** When provided and messageId is set, clicking the quote scrolls to the source message */
  onScrollToSource?: (messageId: string, text: string) => void
}

export const QuotedText: React.FC<QuotedTextProps> = ({
  text,
  messageId,
  onClose,
  onScrollToSource
}) => {
  // Only truncate if text exists and is longer than 150 characters
  const truncatedText = text && text.length > 150 ? text.slice(0, 150) + '...' : text || ''
  const canScrollToSource = Boolean(messageId && onScrollToSource)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98, y: -5 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: -5 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
        mass: 0.6,
        duration: 0.4
      }}
      className="mb-2 flex max-w-full items-start gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-100/90 p-3 text-sm shadow-sm backdrop-blur-sm dark:border-neutral-700 dark:bg-neutral-800/90"
    >
      <IconMessageReply size={16} className="mt-1 shrink-0 text-blue-500 dark:text-blue-400" />
      <button
        type="button"
        onClick={() => {
          if (canScrollToSource && messageId) onScrollToSource?.(messageId, text)
        }}
        disabled={!canScrollToSource}
        title={canScrollToSource ? 'Scroll to source message' : undefined}
        className={cn(
          'line-clamp-2 flex-1 break-words text-left text-xs italic text-gray-600 dark:text-gray-400',
          canScrollToSource && 'cursor-pointer hover:text-gray-800 dark:hover:text-gray-200'
        )}
      >
        &quot; {truncatedText} &quot;
      </button>
      <button
        onClick={onClose}
        className="hover:text-foreground shrink-0 touch-manipulation rounded-full p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-neutral-700"
        aria-label="Remove quote"
      >
        <IconX size={16} />
      </button>
    </motion.div>
  )
}
